/**
 * WorldManager — local multi-world save/load.
 *
 * Each world has its own seed and its own persisted chunks + snap points, stored
 * in IndexedDB. Only EDITED chunks are persisted (saved on edit/undo, and reloaded in
 * preference to regeneration); unedited terrain is a pure function of the seed + gen
 * config and is regenerated on every load. The last active world id is remembered in
 * localStorage and restored on page load.
 *
 * Local (offline) mode only — the server path is unaffected.
 */

import { defaultGenConfig, GEN_CONFIG_VERSION, type WorldGenConfig } from '@worldify/shared';
import { perfStats } from '../debug/PerformanceStats';

// ============== Types ==============

export interface WorldMeta {
  id: string;
  name: string;
  seed: number;
  createdAt: number;
  lastPlayedAt: number;
  /** Last player position + look, restored on load / world switch (optional). */
  lastPos?: { x: number; y: number; z: number };
  lastYaw?: number;
  lastPitch?: number;
  /** Persisted time-of-day (0-1), restored on load / world switch (optional). */
  timeOfDay?: number;
  /**
   * Per-world generation settings — the node tree (docs/node-graph-refactor-plan.md C3). A world
   * WITHOUT one predates the tree and cannot be generated by this build; `listWorlds` deletes it.
   */
  genConfig?: WorldGenConfig;
}

/** Player position + look snapshot for persistence. */
export interface PlayerPose { x: number; y: number; z: number; yaw: number; pitch: number; }

// ============== IndexedDB ==============

const DB_NAME = 'worldify-worlds';
// v3: voxel word widened 16-bit → 32-bit. Old Uint16Array chunk records are
// migrated lazily on load (see loadChunk) — no onupgradeneeded rewrite needed.
// v5: the LOD edit cascade's three stores (layers / clips / pending) — new stores only,
// nothing existing is rewritten, and a world with none of them simply has no coarse edits.
// v6: the 'columns' store is gone. Column heights were a revisit-speed cache, but once
// unedited chunks regenerate on load, stale cached heights against current-generator
// chunks mis-range column requests (rows holding the real surface classified sky → holes).
const DB_VERSION = 6;
const STORE_WORLDS = 'worlds';   // keyPath 'id' → WorldMeta
const STORE_CHUNKS = 'chunks';   // key `${worldId}:${chunkKey}` → Uint32Array
const STORE_SNAPS = 'snaps';     // key worldId → {x,y,z}[]
const STORE_UNDO = 'undo';       // key worldId → UndoEntry[]
const STORE_LOD_LAYERS = 'lodLayers';   // key `${worldId}:${cx},${cy},${cz},${lod}` → SavedLodLayer
const STORE_LOD_CLIPS = 'lodClips';     // key `${worldId}:${cx},${cz},${lod}` → SavedLodClip
const STORE_LOD_PENDING = 'lodPending'; // key worldId → SavedLodPending[]

const ACTIVE_WORLD_KEY = 'worldify-active-world';
const DEFAULT_SEED = 12345;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_WORLDS)) db.createObjectStore(STORE_WORLDS, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(STORE_CHUNKS)) db.createObjectStore(STORE_CHUNKS);
      if (!db.objectStoreNames.contains(STORE_SNAPS)) db.createObjectStore(STORE_SNAPS);
      if (!db.objectStoreNames.contains(STORE_UNDO)) db.createObjectStore(STORE_UNDO);
      if (!db.objectStoreNames.contains(STORE_LOD_LAYERS)) db.createObjectStore(STORE_LOD_LAYERS);
      if (!db.objectStoreNames.contains(STORE_LOD_CLIPS)) db.createObjectStore(STORE_LOD_CLIPS);
      if (!db.objectStoreNames.contains(STORE_LOD_PENDING)) db.createObjectStore(STORE_LOD_PENDING);
      // v6: reclaim the retired column-heights cache.
      if (db.objectStoreNames.contains('columns')) db.deleteObjectStore('columns');
    };
  });
  return dbPromise;
}

function idbGet<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  return openDB().then(db => new Promise<T | undefined>((resolve, reject) => {
    const req = db.transaction(store, 'readonly').objectStore(store).get(key);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result as T | undefined);
  }));
}

function idbPut(store: string, value: unknown, key?: IDBValidKey): Promise<void> {
  return openDB().then(db => new Promise<void>((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    key === undefined ? tx.objectStore(store).put(value) : tx.objectStore(store).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}

// ============== Coalesced chunk writes ==============
//
// saveChunk used to open ONE IndexedDB transaction PER CHUNK, synchronously, inside the
// worker callback that delivered the chunk — i.e. in the frame gap, in no budget. A pan streams a
// few hundred chunks, so that was a few hundred transactions (~0.7 ms each of main-thread time in
// the profile: `put` + `transaction` + the clone) landing wherever the workers happened to finish.
// Writes are queued here and flushed in ONE transaction every PERSIST_FLUSH_MS, and on pagehide.
// The full IDB key is captured at enqueue time, so a world switch mid-window still writes under the
// world the chunk belonged to; purgeWorld drops that world's pending writes before deleting.

const PERSIST_FLUSH_MS = 250;
const pendingChunkWrites = new Map<string, Uint32Array>();
let persistFlushTimer: ReturnType<typeof setTimeout> | null = null;

function schedulePersistFlush(): void {
  if (persistFlushTimer === null) persistFlushTimer = setTimeout(flushPersistQueue, PERSIST_FLUSH_MS);
}

/** Write every queued chunk now, in one transaction. Safe to call when nothing is queued. */
export function flushPersistQueue(): void {
  if (persistFlushTimer !== null) { clearTimeout(persistFlushTimer); persistFlushTimer = null; }
  if (pendingChunkWrites.size === 0) return;
  const chunks = [...pendingChunkWrites]; pendingChunkWrites.clear();
  openDB().then((db) => {
    perfStats.begin('offFrame'); // the puts clone every payload on the main thread, between frames
    perfStats.begin('offIdb');
    const tx = db.transaction(STORE_CHUNKS, 'readwrite');
    const cs = tx.objectStore(STORE_CHUNKS);
    for (const [k, v] of chunks) cs.put(v, k);
    perfStats.end('offIdb');
    perfStats.end('offFrame');
  }).catch(() => { /* non-critical, as the per-chunk writes were */ });
}

/** Forget queued writes for a world (before purging it). */
function dropPendingWrites(worldId: string): void {
  const prefix = `${worldId}:`;
  for (const k of pendingChunkWrites.keys()) if (k.startsWith(prefix)) pendingChunkWrites.delete(k);
}

if (typeof window !== 'undefined') window.addEventListener('pagehide', flushPersistQueue);

/** Every record in `store` whose key starts with `prefix`, paired with the key's remainder. */
function idbGetAllPrefixed<T>(store: string, prefix: string): Promise<Array<{ id: string; value: T }>> {
  return openDB().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const os = tx.objectStore(store);
    const range = IDBKeyRange.bound(prefix, `${prefix}\uffff`);
    const keysReq = os.getAllKeys(range);
    const valsReq = os.getAll(range);
    tx.onerror = () => reject(tx.error);
    tx.oncomplete = () => {
      const keys = keysReq.result as string[];
      const vals = valsReq.result as T[];
      resolve(keys.map((k, i) => ({ id: k.slice(prefix.length), value: vals[i] })));
    };
  }));
}

function idbGetAll<T>(store: string): Promise<T[]> {
  return openDB().then(db => new Promise<T[]>((resolve, reject) => {
    const req = db.transaction(store, 'readonly').objectStore(store).getAll();
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result as T[]);
  }));
}

// ============== Module state ==============

let activeWorld: WorldMeta | null = null;
/** Bare chunk keys ("cx,cy,cz") persisted for the ACTIVE world — sync existence check. */
let persistedKeys = new Set<string>();
/**
 * Per-column ("cx,cz") vertical span of the persisted (= edited) chunks. Column heights are not
 * persisted, so a reload derives a column's chunk range from generated terrain alone — and a build
 * ABOVE that terrain sits in rows classified sky, its persisted chunks never requested. This is the
 * lod-0 counterpart of the coarse lod_clips store, derived from the chunk keys instead of stored.
 */
let editedColumnRanges = new Map<string, { minCy: number; maxCy: number }>();

function widenEditedColumnRange(key: string): void {
  const [cx, cy, cz] = key.split(',').map(Number);
  const ck = `${cx},${cz}`;
  const range = editedColumnRanges.get(ck);
  if (!range) editedColumnRanges.set(ck, { minCy: cy, maxCy: cy });
  else {
    if (cy < range.minCy) range.minCy = cy;
    if (cy > range.maxCy) range.maxCy = cy;
  }
}

/** Sync: the vertical chunk-row span of the active world's edited chunks in this column, if any. */
export function editedChunkRange(cx: number, cz: number): { minCy: number; maxCy: number } | undefined {
  return editedColumnRanges.get(`${cx},${cz}`);
}
/** Undo stack for the ACTIVE world (before-images of build ops), persisted to IDB. */
let undoStack: UndoEntry[] = [];
const UNDO_MAX = 20;
/** Called after the active world changes so the game can rebuild the terrain. */
let onWorldSwitch: (() => void) | null = null;

export function setWorldSwitchHandler(cb: () => void): void {
  onWorldSwitch = cb;
}

/** Supplies the current player pose so the OUTGOING world can be saved on switch. */
let posProvider: (() => PlayerPose) | null = null;

export function setPlayerPosProvider(cb: () => PlayerPose): void {
  posProvider = cb;
}

/** Supplies the current time-of-day so the OUTGOING world can be saved on switch. */
let timeProvider: (() => number) | null = null;

export function setTimeOfDayProvider(cb: () => number): void {
  timeProvider = cb;
}

/** Listeners notified when the world list or active world changes (for UI refresh). */
const worldsChangedCbs = new Set<() => void>();

export function subscribeWorldsChanged(cb: () => void): () => void {
  worldsChangedCbs.add(cb);
  return () => { worldsChangedCbs.delete(cb); };
}

function notifyWorldsChanged(): void {
  for (const cb of worldsChangedCbs) { try { cb(); } catch { /* ignore */ } }
}

// ============== World lifecycle ==============

export async function initWorlds(): Promise<WorldMeta> {
  try {
    const worlds = await listWorlds();
    if (worlds.length === 0) {
      activeWorld = await createWorld('World 1', DEFAULT_SEED);
    } else {
      const savedId = localStorage.getItem(ACTIVE_WORLD_KEY);
      activeWorld =
        worlds.find(w => w.id === savedId) ??
        worlds.slice().sort((a, b) => b.lastPlayedAt - a.lastPlayedAt)[0];
    }
    await activate(activeWorld);
    return activeWorld;
  } catch (e) {
    // IndexedDB unavailable (e.g. private mode): fall back to an in-memory world
    // so the game still boots — it just regenerates from the seed each load.
    console.warn('[WorldManager] persistence unavailable, using in-memory world', e);
    activeWorld = { id: 'default', name: 'World 1', seed: DEFAULT_SEED, createdAt: Date.now(), lastPlayedAt: Date.now() };
    persistedKeys = new Set();
    editedColumnRanges = new Map();
    return activeWorld;
  }
}

/**
 * THE ONLY MIGRATION IN THE SYSTEM, and it is a deletion.
 *
 * A world without a `genConfig`, or with one older than GEN_CONFIG_VERSION, was created before a
 * generation rebuild (C0/C3 of docs/node-graph-refactor-plan.md, then the tree rework). Its
 * persisted chunks were written by a generator that no longer exists and its stored settings name
 * kinds that are not registered, so there is nothing to migrate TO. Worse than nothing for v1
 * specifically: `parse` would happily read it and hand back a LANDFORM world where the player had
 * built on flat ground, because the enum that said flat is gone and `landform.enabled` defaults on.
 * Keeping it listed would offer the player a world that renders as something else entirely.
 *
 * So it is removed, with everything belonging to it, and the count is reported once so the deletion
 * is visible rather than silent. `WorldGenConfig.version` exists from here on precisely so that the
 * NEXT change gets a migration instead of another one of these.
 */
export async function listWorlds(): Promise<WorldMeta[]> {
  const all = await idbGetAll<WorldMeta>(STORE_WORLDS);
  const current = (w: WorldMeta) => !!w.genConfig && w.genConfig.version >= GEN_CONFIG_VERSION;
  const stale = all.filter((w) => !current(w));
  if (stale.length === 0) return all;
  console.warn(`[worlds] removing ${stale.length} world(s) created before the generation rebuild:`,
    stale.map((w) => w.name).join(', '));
  for (const w of stale) await purgeWorld(w.id);
  return all.filter(current);
}

export function getActiveWorld(): WorldMeta | null {
  return activeWorld;
}

export function getActiveWorldSeed(): number {
  return activeWorld?.seed ?? DEFAULT_SEED;
}

/** The active world's generation settings — the node tree it was created with. */
export function getActiveWorldGenConfig(): WorldGenConfig | undefined {
  return activeWorld?.genConfig;
}

/** A fresh random world seed (31-bit), matching createWorld's default. */
export function randomWorldSeed(): number {
  return Math.floor(Math.random() * 0x7fffffff);
}

/** The default name a new world would get ("World N"), for pre-filling the create dialog. */
export async function nextWorldName(): Promise<string> {
  return `World ${(await listWorlds()).length + 1}`;
}

export async function createWorld(
  name?: string, seed?: number, genConfig?: WorldGenConfig,
): Promise<WorldMeta> {
  const existing = await listWorlds();
  const world: WorldMeta = {
    id: crypto.randomUUID(),
    name: name?.trim() || `World ${existing.length + 1}`,
    seed: seed ?? randomWorldSeed(),
    createdAt: Date.now(),
    lastPlayedAt: Date.now(),
    genConfig: genConfig ?? defaultGenConfig(seed ?? randomWorldSeed()),
  };
  await idbPut(STORE_WORLDS, world);
  return world;
}

/** Create a new world AND make it active (rebuilds terrain). */
export async function createAndActivateWorld(
  name?: string, seed?: number, genConfig?: WorldGenConfig,
): Promise<WorldMeta> {
  const world = await createWorld(name, seed, genConfig);
  await setActiveWorld(world.id);
  return world;
}

/** Switch the active world and rebuild the terrain via the registered handler. */
export async function setActiveWorld(id: string): Promise<void> {
  if (activeWorld?.id === id) return;
  // Snapshot the OUTGOING world's player pose before we swap active worlds — activate()
  // reassigns `activeWorld`, so this must run first (the game-side switch handler is
  // too late). Writes the current (old) world; activate() then loads the new one's pose.
  if (activeWorld && posProvider) {
    const p = posProvider();
    savePlayerPos(p);
  }
  if (activeWorld && timeProvider) {
    saveTimeOfDay(timeProvider());
  }
  const world = await idbGet<WorldMeta>(STORE_WORLDS, id);
  if (!world) return;
  await activate(world);
  onWorldSwitch?.();
}

export async function deleteWorld(id: string): Promise<void> {
  const worlds = await listWorlds();
  if (worlds.length <= 1) return; // never delete the last world
  await purgeWorld(id);
  // If we deleted the active world, activate the most-recently-played remaining one.
  if (activeWorld?.id === id) {
    const remaining = (await listWorlds()).sort((a, b) => b.lastPlayedAt - a.lastPlayedAt);
    await activate(remaining[0]);
    onWorldSwitch?.();
  }
}

/** Remove a world's meta and every row that belongs to it. */
async function purgeWorld(id: string): Promise<void> {
  dropPendingWrites(id);
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([STORE_WORLDS, STORE_SNAPS, STORE_CHUNKS, STORE_UNDO,
      STORE_LOD_LAYERS, STORE_LOD_CLIPS, STORE_LOD_PENDING], 'readwrite');
    tx.objectStore(STORE_WORLDS).delete(id);
    tx.objectStore(STORE_SNAPS).delete(id);
    tx.objectStore(STORE_UNDO).delete(id);
    tx.objectStore(STORE_LOD_PENDING).delete(id);
    const range = IDBKeyRange.bound(`${id}:`, `${id}:￿`);
    for (const storeName of [STORE_CHUNKS, STORE_LOD_LAYERS, STORE_LOD_CLIPS]) {
      const store = tx.objectStore(storeName);
      const cur = store.openKeyCursor(range);
      cur.onsuccess = () => {
        const c = cur.result;
        if (c) { store.delete(c.primaryKey); c.continue(); }
      };
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Internal: set active world, persist last-active id, touch lastPlayedAt, preload keys. */
async function activate(world: WorldMeta): Promise<void> {
  activeWorld = world;
  world.lastPlayedAt = Date.now();
  try { localStorage.setItem(ACTIVE_WORLD_KEY, world.id); } catch { /* ignore */ }
  await idbPut(STORE_WORLDS, world);
  await preloadChunkKeys(world.id);
  undoStack = normalizeUndoStack(await idbGet<unknown>(STORE_UNDO, world.id));
  notifyWorldsChanged();
}

async function preloadChunkKeys(worldId: string): Promise<void> {
  persistedKeys = new Set();
  editedColumnRanges = new Map();
  const db = await openDB();
  const range = IDBKeyRange.bound(`${worldId}:`, `${worldId}:￿`);
  await new Promise<void>((resolve, reject) => {
    const req = db.transaction(STORE_CHUNKS, 'readonly').objectStore(STORE_CHUNKS).getAllKeys(range);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => {
      const prefix = `${worldId}:`;
      for (const k of req.result as string[]) {
        const key = k.slice(prefix.length);
        persistedKeys.add(key);
        widenEditedColumnRange(key);
      }
      resolve();
    };
  });
}

// ============== Chunk persistence (active world) ==============

/** Sync: is this chunk already persisted for the active world? */
export function hasChunk(key: string): boolean {
  return persistedKeys.has(key);
}

export async function loadChunk(key: string): Promise<Uint32Array | null> {
  if (!activeWorld) return null;
  // A chunk saved within the last flush window is only in the queue so far — hasChunk already says
  // it is persisted, and a miss here would regenerate it for nothing.
  const queued = pendingChunkWrites.get(`${activeWorld.id}:${key}`);
  if (queued) return queued;
  const buf = await idbGet<Uint16Array | Uint32Array>(STORE_CHUNKS, `${activeWorld.id}:${key}`);
  if (!buf) return null;
  // Migrate legacy 16-bit records: widen element-wise into 32-bit words (block/spare
  // bits become 0; both light channels are recomputed client-side on ingest anyway).
  return buf.BYTES_PER_ELEMENT === 4 ? (buf as Uint32Array) : new Uint32Array(buf);
}

/** Fire-and-forget save (clones the data — chunk.data is mutated in place). */
export function saveChunk(key: string, data: Uint32Array): void {
  if (!activeWorld) return;
  persistedKeys.add(key);
  widenEditedColumnRange(key);
  pendingChunkWrites.set(`${activeWorld.id}:${key}`, new Uint32Array(data));
  schedulePersistFlush();
}

// Column heights are NOT persisted: they were a revisit-speed cache, but heights drive which
// chunk rows a column requests, and once unedited chunks regenerate on load (see loadLocalChunk)
// a cached height from an older generator against current-generator chunks is a hole factory.
// Every load re-derives them from the same worker generation that produces the chunks.

// ============== LOD edit-layer persistence (active world) ==============
//
// The coarse half of a player's builds (see LodEditLayers). Three stores, all sparse:
//  - layers:  one record per coarse chunk that carries edits — two typed arrays, 6 bytes a voxel.
//  - clips:   one record per coarse column whose vertical clip a build widened. Without it the
//             chunk holding a tower is classified sky on reload, never requested, and the layer
//             never applies: an invisible build and a coverage hole.
//  - pending: the edit boxes whose fold has not run yet, so a tab closed inside the cascade's
//             debounce replays them next session instead of leaving a stale far view. One record
//             per world (the list is short-lived and always rewritten whole).

export interface SavedLodLayer {
  cx: number; cy: number; cz: number; lod: number;
  idx: Uint16Array; words: Uint32Array;
}
export interface SavedLodClip { cx: number; cz: number; lod: number; minCy: number; maxCy: number }
export interface SavedLodPending {
  cx: number; cy: number; cz: number;
  box: { x0: number; y0: number; z0: number; x1: number; y1: number; z1: number };
}

/** Everything the cascade needs at world activation. Empty for a world with no coarse edits. */
export async function loadLodEditState(): Promise<{
  layers: SavedLodLayer[]; clips: SavedLodClip[]; pending: SavedLodPending[];
}> {
  const empty = { layers: [], clips: [], pending: [] };
  if (!activeWorld) return empty;
  const prefix = `${activeWorld.id}:`;
  try {
    const [layerRecs, clipRecs, pending] = await Promise.all([
      idbGetAllPrefixed<{ idx: Uint16Array; words: Uint32Array }>(STORE_LOD_LAYERS, prefix),
      idbGetAllPrefixed<{ minCy: number; maxCy: number }>(STORE_LOD_CLIPS, prefix),
      idbGet<SavedLodPending[]>(STORE_LOD_PENDING, activeWorld.id),
    ]);
    const layers: SavedLodLayer[] = [];
    for (const rec of layerRecs) {
      const [cx, cy, cz, lod] = rec.id.split(',').map(Number);
      if (!rec.value || !Number.isFinite(lod)) continue;
      layers.push({ cx, cy, cz, lod, idx: rec.value.idx, words: rec.value.words });
    }
    const clips: SavedLodClip[] = [];
    for (const rec of clipRecs) {
      const [cx, cz, lod] = rec.id.split(',').map(Number);
      if (!rec.value || !Number.isFinite(lod)) continue;
      clips.push({ cx, cz, lod, minCy: rec.value.minCy, maxCy: rec.value.maxCy });
    }
    return { layers, clips, pending: pending ?? [] };
  } catch {
    return empty;
  }
}

/** Fire-and-forget: one cascade's layers + clips, in a single transaction. */
export function saveLodEdits(layers: SavedLodLayer[], clips: SavedLodClip[]): void {
  if (!activeWorld || (layers.length === 0 && clips.length === 0)) return;
  const worldId = activeWorld.id;
  openDB().then(db => {
    const tx = db.transaction([STORE_LOD_LAYERS, STORE_LOD_CLIPS], 'readwrite');
    const layerStore = tx.objectStore(STORE_LOD_LAYERS);
    for (const l of layers) {
      layerStore.put({ idx: l.idx, words: l.words }, `${worldId}:${l.cx},${l.cy},${l.cz},${l.lod}`);
    }
    const clipStore = tx.objectStore(STORE_LOD_CLIPS);
    for (const c of clips) {
      clipStore.put({ minCy: c.minCy, maxCy: c.maxCy }, `${worldId}:${c.cx},${c.cz},${c.lod}`);
    }
  }).catch(() => { /* non-critical: the pending markers replay the fold */ });
}

/** Fire-and-forget: the not-yet-folded edit boxes (an empty list clears the record). */
export function saveLodPending(list: SavedLodPending[]): void {
  if (!activeWorld) return;
  idbPut(STORE_LOD_PENDING, list, activeWorld.id).catch(() => { /* non-critical */ });
}

// ============== Snap-point persistence (active world) ==============

export interface SnapPoint { x: number; y: number; z: number; }

export async function loadSnapPoints(): Promise<SnapPoint[]> {
  if (!activeWorld) return [];
  return (await idbGet<SnapPoint[]>(STORE_SNAPS, activeWorld.id)) ?? [];
}

export function saveSnapPoints(points: SnapPoint[]): void {
  if (!activeWorld) return;
  idbPut(STORE_SNAPS, points, activeWorld.id).catch(() => { /* non-critical */ });
}

// ============== Player-pose persistence (active world) ==============

/**
 * Persist the player's position + look on the active world's meta row (fire-and-forget).
 * Stored as fields on WorldMeta so no schema change is needed; loaded back in activate().
 */
export function savePlayerPos(pose: PlayerPose): void {
  if (!activeWorld) return;
  activeWorld.lastPos = { x: pose.x, y: pose.y, z: pose.z };
  activeWorld.lastYaw = pose.yaw;
  activeWorld.lastPitch = pose.pitch;
  idbPut(STORE_WORLDS, activeWorld).catch(() => { /* non-critical */ });
}

/** The active world's saved player pose, or null if it has none (e.g. a new world). */
export function loadPlayerPos(): PlayerPose | null {
  const w = activeWorld;
  if (!w || !w.lastPos) return null;
  return { x: w.lastPos.x, y: w.lastPos.y, z: w.lastPos.z, yaw: w.lastYaw ?? 0, pitch: w.lastPitch ?? 0 };
}

/** Persist the active world's time-of-day (0-1), fire-and-forget. */
export function saveTimeOfDay(timeOfDay: number): void {
  if (!activeWorld) return;
  activeWorld.timeOfDay = timeOfDay;
  idbPut(STORE_WORLDS, activeWorld).catch(() => { /* non-critical */ });
}

/** The active world's saved time-of-day, or null if it has none. */
export function loadTimeOfDay(): number | null {
  return activeWorld?.timeOfDay ?? null;
}

// ============== Undo stack (active world) ==============

/** A chunk's voxel data before a build op — enough to reverse it. */
export interface ChunkSnapshot { key: string; data: Uint32Array; }
export interface UndoEntry {
  chunks: ChunkSnapshot[];
  /** Snap points the reversed placement deposited, so undo takes them back out too. */
  snapPoints?: SnapPoint[];
}

/** Pre-snap-point entries persisted as a bare snapshot array. */
function normalizeUndoStack(raw: unknown): UndoEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((e) => (Array.isArray(e) ? { chunks: e as ChunkSnapshot[] } : e as UndoEntry));
}

export function pushUndo(entry: UndoEntry): void {
  undoStack.push(entry);
  while (undoStack.length > UNDO_MAX) undoStack.shift();
  saveUndo();
}

/** Record the snap points a placement deposited on the entry that reverses it. */
export function attachUndoSnapPoints(points: SnapPoint[]): void {
  if (points.length === 0 || undoStack.length === 0) return;
  undoStack[undoStack.length - 1].snapPoints = points;
  saveUndo();
}

export function popUndo(): UndoEntry | undefined {
  const entry = undoStack.pop();
  saveUndo();
  return entry;
}

export function canUndo(): boolean {
  return undoStack.length > 0;
}

function saveUndo(): void {
  if (!activeWorld) return;
  idbPut(STORE_UNDO, undoStack, activeWorld.id).catch(() => { /* non-critical */ });
}
